import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxErrorsModule } from '@ultimate/ngxerrors';
import { OwlDateTimeModule } from 'ng-pick-datetime';
import { ModalModule } from 'ngx-boot-modal';
import { NgxPaginationModule } from 'ngx-pagination';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { AutocompleteFieldComponent } from './components/forms/autocomplete-field/autocomplete-field.component';
import { CheckboxFieldComponent } from './components/forms/checkbox-field/checkbox-field.component';
import { DatepickerFieldComponent } from './components/forms/datepicker-field/datepicker-field.component';
import { FieldErrorsComponent } from './components/forms/field-errors/field-errors.component';
import { FileFieldComponent } from './components/forms/file-field/file-field.component';
import { FileInputComponent } from './components/forms/file-input/file-input.component';
import { InputEmailFieldComponent } from './components/forms/input-email-field/input-email-field.component';
import { InputFieldComponent } from './components/forms/input-field/input-field.component';
import { InputIntegerFieldComponent } from './components/forms/input-integer-field/input-integer-field.component';
import { InputNumberFieldComponent } from './components/forms/input-number-field/input-number-field.component';
import { InputPasswordFieldComponent } from './components/forms/input-password-field/input-password-field.component';
import { InputRangeFieldComponent } from './components/forms/input-range-field/input-range-field.component';
import { InputTextFieldComponent } from './components/forms/input-text-field/input-text-field.component';
import { InputTimeFieldComponent } from './components/forms/input-time-field/input-time-field.component';
import { RadioFieldComponent } from './components/forms/radio-field/radio-field.component';
import { SelectFieldComponent } from './components/forms/select-field/select-field.component';
import { TextareaFieldComponent } from './components/forms/textarea-field/textarea-field.component';
import { ConfirmModalComponent } from './components/modals/confirm-modal/confirm-modal.component';
import { EditionModalComponent } from './components/modals/edition-modal/edition-modal.component';
import { InfoModalComponent } from './components/modals/info-modal/info-modal.component';
import { SortControlComponent } from './components/sort-control/sort-control.component';
import { FormCellComponent } from './components/tables/form-table/form-cell/form-cell.component';
import { FormColumnComponent } from './components/tables/form-table/form-column/form-column.component';
import { FormTableColumnComponent } from './components/tables/form-table/form-table-column/form-table-column.component';
import { FormTableComponent } from './components/tables/form-table/form-table.component';
import { ColumnComponent } from './components/tables/sortable-table/column/column.component';
import { SortableCellComponent } from './components/tables/sortable-table/sortable-cell/sortable-cell.component';
import { SortableColumnComponent } from './components/tables/sortable-table/sortable-column/sortable-column.component';
import { SortableTableComponent } from './components/tables/sortable-table/sortable-table.component';
import { IntegerInputDirective } from './directives/integer-input.directive';
import { TitleDirective } from './directives/title.directive';
import { CapitalizeAllPipe } from './pipes/capitalize-all.pipe';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { DayDiffPipe } from './pipes/day-diff.pipe';
import { IntLoopPipe } from './pipes/int-loop.pipe';
import { LocalizedCurrencyPipe } from './pipes/localized-currency.pipe';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { PercentagePipe } from './pipes/percentage.pipe';
import { SortPipe } from './pipes/sort.pipe';
import { TimePipe } from './pipes/time.pipe';
import { PaginationManagerComponent } from './components/managers/pagination-manager/pagination-manager.component';
import { PaginationSummaryComponent } from './components/managers/pagination-summary/pagination-summary.component';
import { DatepickerModalComponent } from './components/modals/datepicker-modal/datepicker-modal.component';
import { LocalizedDecimalPipe } from './pipes/localized-decimal.pipe';

@NgModule({
  declarations: [
    // Components
    AutocompleteFieldComponent,
    CheckboxFieldComponent,
    ColumnComponent,
    ConfirmModalComponent,
    DatepickerFieldComponent,
    DatepickerModalComponent,
    EditionModalComponent,
    FieldErrorsComponent,
    FileFieldComponent,
    FileInputComponent,
    FormCellComponent,
    FormColumnComponent,
    FormTableColumnComponent,
    FormTableComponent,
    InfoModalComponent,
    InputEmailFieldComponent,
    InputFieldComponent,
    InputIntegerFieldComponent,
    InputNumberFieldComponent,
    InputPasswordFieldComponent,
    InputTextFieldComponent,
    InputTimeFieldComponent,
    InputRangeFieldComponent,
    PaginationManagerComponent,
    PaginationSummaryComponent,
    RadioFieldComponent,
    SelectFieldComponent,
    SortableColumnComponent,
    SortableCellComponent,
    SortableTableComponent,
    SortControlComponent,
    TextareaFieldComponent,
    // Pipes
    CapitalizePipe,
    CapitalizeAllPipe,
    DayDiffPipe,
    IntLoopPipe,
    LocalizedCurrencyPipe,
    LocalizedDatePipe,
    LocalizedDecimalPipe,
    PercentagePipe,
    SortPipe,
    TimePipe,
    // Directives
    IntegerInputDirective,
    TitleDirective
  ],
  imports: [
    CommonModule,
    ModalModule,
    NgSelectModule,
    NgxErrorsModule,
    NgxPaginationModule,
    OwlDateTimeModule,
    PerfectScrollbarModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule
  ],
  exports: [
    // Modules
    CommonModule,
    ModalModule,
    NgxErrorsModule,
    NgxPaginationModule,
    OwlDateTimeModule,
    PerfectScrollbarModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    // Components
    AutocompleteFieldComponent,
    CheckboxFieldComponent,
    ColumnComponent,
    DatepickerFieldComponent,
    DatepickerModalComponent,
    EditionModalComponent,
    FieldErrorsComponent,
    FileFieldComponent,
    FileInputComponent,
    FormTableColumnComponent,
    FormTableComponent,
    InfoModalComponent,
    InputEmailFieldComponent,
    InputFieldComponent,
    InputIntegerFieldComponent,
    InputNumberFieldComponent,
    InputPasswordFieldComponent,
    InputTextFieldComponent,
    InputTimeFieldComponent,
    InputRangeFieldComponent,
    PaginationManagerComponent,
    PaginationSummaryComponent,
    RadioFieldComponent,
    SelectFieldComponent,
    SortableTableComponent,
    SortControlComponent,
    TextareaFieldComponent,
    ConfirmModalComponent,
    // Pipes
    CapitalizeAllPipe,
    CapitalizePipe,
    DayDiffPipe,
    IntLoopPipe,
    LocalizedCurrencyPipe,
    LocalizedDatePipe,
    LocalizedDecimalPipe,
    PercentagePipe,
    SortPipe,
    TimePipe,
    // Directives
    IntegerInputDirective,
    TitleDirective
  ],
  providers: [
    // Pipes
    CurrencyPipe,
    DecimalPipe,
    LocalizedDatePipe,
    LocalizedDecimalPipe,
    LocalizedCurrencyPipe
  ]
})
export class SharedModule { }



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/ubaristi-app-test/workspace/src/app/shared/shared.module.ts